/*
Origin Core 1.0 — Alex Monroe

called from /blocks/_product-buy-buttons.liquid

SUMMARY
--------------------
This script defines a custom web component <variant-availability> that corrects the
PDP stock-status pill above the add-to-cart CTA.

Liquid `variant.inventory_quantity` is the SUM across every stocking location (Website
+ boutique POS locations), so a product that is out of stock at the Website location but
held at a boutique renders as "In stock". To communicate the *Website* availability, we
resolve the true website-location stock at runtime from the same App Proxy that powers
the in-store availability panel (see assets/store-availability.js). The proxy is expected
to return a per-variant boolean:

  { "websiteInStock": true | false, "stockByLocation": { ... } }

Resolution:
  websiteInStock === true  -> In stock     (server "in stock" candidate)
  websiteInStock === false -> the policy-derived fallback candidate the server rendered
                              with `ignore_inventory: true` (Made to order / Out of stock)

While the request is in flight the pill shows a neutral "Checking availability…" state so
there is never a flash of the wrong status. When the request fails or omits `websiteInStock`, a neutral unavailable message is
shown instead of falling back to stock from other locations. With no proxy configured,
the server-rendered badge is left untouched. Cart rows use the same resolver.

The component re-checks on `variant:update`, reading the freshly server-rendered candidate
data from the event's section HTML, and aborts stale requests so only the latest variant's
result is shown.
*/

import { getWebsiteStock } from './website-stock.js';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const ICON_TICKMARK = 'tickmark';

class VariantAvailability extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;
  /** @type {Element | null} */
  #section = null;
  /** @type {((event: Event) => void) | undefined} */
  #variantUpdateHandler;
  /** @type {(() => void) | undefined} */
  #variantSelectedHandler;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.hasAttribute('data-cart-availability') ? null : this.closest('.shopify-section, dialog');
    if (this.#section) {
      this.#variantUpdateHandler = (event) => this.#onVariantUpdate(/** @type {CustomEvent} */ (event));
      this.#section.addEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);

      // The moment a selection is made (before the section re-renders) show the
      // neutral checking state so the stale all-locations guess never flashes.
      this.#variantSelectedHandler = () => {
        this.#activeFetch?.abort();
        if (this.dataset.proxyUrl) this.#applyChecking();
      };
      this.#section.addEventListener(ThemeEvents.variantSelected, this.#variantSelectedHandler);
    }

    this.#check(this.dataset.variantId);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#check(this.dataset.variantId);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#section) {
      if (this.#variantUpdateHandler) {
        this.#section.removeEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
      }
      if (this.#variantSelectedHandler) {
        this.#section.removeEventListener(ThemeEvents.variantSelected, this.#variantSelectedHandler);
      }
    }
    this.#activeFetch?.abort();
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate(event) {
    const detail = event.detail || {};
    const resource = detail.resource;
    /** @type {Document | undefined} */
    const html = detail.data && detail.data.html;

    const selectedId = resource?.id == null ? '' : String(resource.id);
    const productId = detail.data?.newProduct?.id || detail.data?.productId;
    const owner = this.closest('product-form-component');
    if (productId && owner?.dataset.productId && String(productId) !== owner.dataset.productId) return;

    this.#activeFetch?.abort();
    this.dataset.variantId = selectedId;
    if (!selectedId) {
      this.#applyUnavailable();
      return;
    }

    // The response can contain the header mini-cart before the PDP. Its first badge
    // may describe the variant originally added to the bag, not the new selection.
    if (html) {
      const fresh = Array.from(html.querySelectorAll('variant-availability:not([data-cart-availability])'))
        .find((candidate) => {
          if (!(candidate instanceof HTMLElement) || candidate.dataset.variantId !== selectedId) return false;
          const candidateProductId = candidate.closest('product-form-component')?.dataset.productId;
          return !productId || !candidateProductId || candidateProductId === String(productId);
        });
      if (!(fresh instanceof HTMLElement)) {
        // Do not reuse another size's MTO lead time when matching markup is missing.
        this.dataset.variantId = '';
        this.dataset.fallbackText = '';
        this.dataset.fallbackIcon = '';
        this.dataset.fallbackType = '';
        this.#applyUnavailable();
        return;
      }
      this.dataset.inStockText = fresh.dataset.inStockText || '';
      this.dataset.inStockIcon = fresh.dataset.inStockIcon || ICON_TICKMARK;
      this.dataset.fallbackText = fresh.dataset.fallbackText || '';
      this.dataset.fallbackIcon = fresh.dataset.fallbackIcon || '';
      this.dataset.fallbackType = fresh.dataset.fallbackType || '';
    }

    this.#check(this.dataset.variantId);
  }

  /**
   * Resolves the website-location availability for a variant from the App Proxy.
   * @param {string | undefined} variantId
   */
  #check(variantId) {
    const proxyUrl = this.dataset.proxyUrl;

    // No proxy configured or no variant — leave the server-rendered badge as is.
    if (!proxyUrl || !variantId) return;

    this.#applyChecking();

    const abortController = this.#createAbortController();
    // The controller cancels this consumer only; other badges share the request.
    getWebsiteStock(proxyUrl, variantId)
      .then((inStock) => {
        if (abortController.signal.aborted) return;
        this.#resolve(inStock);
      })
      .catch((_error) => {
        if (abortController.signal.aborted) return;
        this.#applyUnavailable();
      });
  }

  /**
   * @param {boolean} websiteInStock
   */
  #resolve(websiteInStock) {
    if (websiteInStock) {
      this.#applyState({
        text: this.dataset.inStockText || '',
        icon: this.dataset.inStockIcon || ICON_TICKMARK,
        type: 'in_stock',
      });
    } else {
      this.#applyState({
        text: this.dataset.fallbackText || '',
        icon: this.dataset.fallbackIcon || '',
        type: this.dataset.fallbackType || 'out_of_stock',
      });
    }
  }

  #applyChecking() {
    this.#applyState({
      text: this.dataset.checkingText || '',
      icon: '',
      type: 'checking',
    });
  }

  #applyUnavailable() {
    // An unavailable website-stock response must never imply boutique stock can ship.
    this.#applyState({ text: this.dataset.unavailableText || '', icon: '', type: 'unknown' });
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  /**
   * Applies a badge state to the live DOM, reusing the same nodes and classes
   * that variant-picker.js #syncRingSizeUi toggles.
   * @param {{ text: string, icon: string, type: string }} state
   */
  #applyState(state) {
    const isOos = state.type === 'out_of_stock';

    const textEl = this.querySelector('[data-ring-size-status-text]');
    if (textEl instanceof HTMLElement) textEl.textContent = state.text;

    const iconWrap = this.querySelector('[data-ring-size-status-icon]');
    if (iconWrap instanceof HTMLElement) {
      iconWrap.classList.toggle('text-[#A60505]', isOos);
      iconWrap.classList.toggle('text-black', !isOos);
      iconWrap.querySelectorAll('[data-status-icon-option]').forEach((opt) => {
        const match = opt instanceof HTMLElement && opt.dataset.statusIconOption === state.icon && state.icon !== '';
        opt.classList.toggle('hidden', !match);
        opt.classList.toggle('inline-flex', match);
      });
    }

    const pill = this.querySelector('[data-ring-size-status-pill]');
    if (pill instanceof HTMLElement) {
      pill.classList.remove('bg-secondary', 'text-muted-black', 'bg-pale-red', 'bg-[#FFF4F4]', 'text-black');
      if (isOos) {
        pill.classList.add('bg-pale-red', 'text-black');
      } else {
        pill.classList.add('bg-secondary', 'text-muted-black');
      }
    }
  }
}

if (!customElements.get('variant-availability')) {
  customElements.define('variant-availability', VariantAvailability);
}
